import React, { useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { FiCheck, FiSave, FiX } from "react-icons/fi";
import { Button } from "../../pages/Acessos/styles";
import { DIAS_SEMANA } from "../../utils/agendamento/calendar";
import { useAuth } from "../../context/AuthContext";
import { podeVerRota } from "../../utils/modules";
import { PAINEIS_DISPONIVEIS } from "../../utils/painelRegistry";
import { FORMATOS_EXPORTACAO } from "../../utils/exportacao";
import {
  BotaoFechar,
  CheckBox,
  CheckboxList,
  CheckRow,
  Field,
  FieldLabel,
  FormRow,
  Input,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitulo,
  Overlay,
  SectionLabel,
  Select,
  ToggleRow,
  ToggleSwitch,
  AvisoImagemComValores,
  EntradaOculta,
} from "./styles";

const FADE = { duration: 0.18 };

/**
 * Linha de marcação acessível: o input real fica invisível mas focável, e o
 * <label> (CheckRow) faz o clique. Antes era `<CheckRow onClick>` com um span
 * decorativo — nada disso era alcançável por teclado.
 */
function LinhaMarcavel({ marcado, aoAlternar, children }) {
  return (
    <CheckRow>
      <EntradaOculta type="checkbox" checked={marcado} onChange={aoAlternar} />
      <CheckBox $active={marcado}>{marcado && <FiCheck size={11} />}</CheckBox>
      <span>{children}</span>
    </CheckRow>
  );
}

export default function ModalNovoAgendamento({ aberto, agendamento, onClose, onSave }) {
  // Só os painéis cuja tela a pessoa abre: mesmo gate do menu (permissão e
  // módulo contratado, ADR-0102) — agendar tela que não abre é exportação vazia.
  const { hasAllPermissions, isMaster, temModulo, temProduto } = useAuth();
  const paineis = useMemo(
    () => PAINEIS_DISPONIVEIS.filter((p) => podeVerRota(p.rota, { hasAllPermissions, isMaster, temModulo, temProduto })),
    [hasAllPermissions, isMaster, temModulo, temProduto],
  );
  const editando = Boolean(agendamento);
  const [nome, setNome] = useState(agendamento?.nome || "");
  const [paineisSel, setPaineisSel] = useState(
    () => new Set((agendamento?.paineis || []).map((p) => p.painelId))
  );
  const [formatosSel, setFormatosSel] = useState(
    () => new Set(agendamento?.formatos || ["pdf"])
  );
  const [tipo, setTipo] = useState(agendamento?.frequencia?.tipo || "diario");
  const [hora, setHora] = useState(agendamento?.frequencia?.hora ?? 8);
  const [minuto, setMinuto] = useState(agendamento?.frequencia?.minuto ?? 0);
  const [diasSemana, setDiasSemana] = useState(
    () => new Set(agendamento?.frequencia?.diasSemana || [1])
  );
  const [diaMes, setDiaMes] = useState(agendamento?.frequencia?.diaMes ?? 1);
  const [subpasta, setSubpasta] = useState(agendamento?.subpasta || "");
  const [ocultarPrecos, setOcultarPrecos] = useState(
    Boolean(agendamento?.ocultarPrecos)
  );

  const toggleSet = (setter, set, key) => {
    const novo = new Set(set);
    if (novo.has(key)) novo.delete(key);
    else novo.add(key);
    setter(novo);
  };

  const formatos = useMemo(() => FORMATOS_EXPORTACAO, []);

  // Uma frase por impedimento, na ordem em que o usuário preenche. Antes o
  // botão só ficava cinza, sem dizer o que faltava — e o caso "semanal sem
  // nenhum dia marcado" nem travava: salvava um agendamento que nunca dispara
  // (proximaSemanal devolve null) e a tela ainda o mostrava como "Ativo".
  const impedimento = !nome.trim()
    ? "Dê um nome ao agendamento."
    : paineisSel.size === 0
    ? "Escolha ao menos um painel."
    : formatosSel.size === 0
    ? "Escolha ao menos um formato."
    : tipo === "semanal" && diasSemana.size === 0
    ? "Marque ao menos um dia da semana — sem isso o agendamento nunca dispara."
    : null;

  function salvar() {
    if (impedimento) return;
    const frequencia = { tipo, hora: Number(hora), minuto: Number(minuto) };
    if (tipo === "semanal") frequencia.diasSemana = Array.from(diasSemana);
    if (tipo === "mensal") frequencia.diaMes = Number(diaMes);

    onSave({
      id: agendamento?.id,
      nome: nome.trim(),
      paineis: Array.from(paineisSel).map((painelId) => ({ painelId, opcoes: {} })),
      formatos: Array.from(formatosSel),
      frequencia,
      subpasta: subpasta.trim(),
      ocultarPrecos,
      ativo: agendamento?.ativo !== false,
    });
  }

  return (
    <AnimatePresence>
      {aberto && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={FADE}
          onClick={onClose}
        >
          <Modal
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={FADE}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitulo>
                {editando ? "Editar agendamento" : "Novo agendamento"}
              </ModalTitulo>
              <BotaoFechar onClick={onClose} aria-label="Fechar">
                <FiX size={18} />
              </BotaoFechar>
            </ModalHeader>

            <Field>
              <FieldLabel>Nome do agendamento</FieldLabel>
              <Input
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Backup diário 8h"
              />
            </Field>

            <SectionLabel>Painéis a exportar</SectionLabel>
            <CheckboxList>
              {paineis.map((p) => {
                const ativo = paineisSel.has(p.id);
                return (
                  <LinhaMarcavel
                    key={p.id}
                    marcado={ativo}
                    aoAlternar={() => toggleSet(setPaineisSel, paineisSel, p.id)}
                  >
                    {p.label}
                  </LinhaMarcavel>
                );
              })}
            </CheckboxList>

            <SectionLabel>Formatos</SectionLabel>
            <CheckboxList>
              {formatos.map((f) => {
                const ativo = formatosSel.has(f.id);
                return (
                  <LinhaMarcavel
                    key={f.id}
                    marcado={ativo}
                    aoAlternar={() => toggleSet(setFormatosSel, formatosSel, f.id)}
                  >
                    {f.label}
                  </LinhaMarcavel>
                );
              })}
            </CheckboxList>

            <SectionLabel>Frequência</SectionLabel>
            <FormRow>
              <Field>
                <FieldLabel>Periodicidade</FieldLabel>
                <Select value={tipo} onChange={(e) => setTipo(e.target.value)}>
                  <option value="diario">Diário</option>
                  <option value="semanal">Semanal</option>
                  <option value="mensal">Mensal</option>
                </Select>
              </Field>
              <Field>
                <FieldLabel>Hora</FieldLabel>
                <Input
                  type="number"
                  min={0}
                  max={23}
                  value={hora}
                  onChange={(e) => setHora(e.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel>Minuto</FieldLabel>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  value={minuto}
                  onChange={(e) => setMinuto(e.target.value)}
                />
              </Field>
            </FormRow>

            {tipo === "semanal" && (
              <>
                <SectionLabel>Dias da semana</SectionLabel>
                <CheckboxList>
                  {DIAS_SEMANA.map((d) => (
                    <LinhaMarcavel
                      key={d.value}
                      marcado={diasSemana.has(d.value)}
                      aoAlternar={() => toggleSet(setDiasSemana, diasSemana, d.value)}
                    >
                      {d.label}
                    </LinhaMarcavel>
                  ))}
                </CheckboxList>
              </>
            )}

            {tipo === "mensal" && (
              <Field style={{ marginTop: 8 }}>
                <FieldLabel>Dia do mês (1-31)</FieldLabel>
                <Input
                  type="number"
                  min={1}
                  max={31}
                  value={diaMes}
                  onChange={(e) => setDiaMes(e.target.value)}
                />
              </Field>
            )}

            <SectionLabel>Opções</SectionLabel>
            <Field>
              <FieldLabel>Subpasta (opcional)</FieldLabel>
              <Input
                value={subpasta}
                onChange={(e) => setSubpasta(e.target.value)}
                placeholder="Ex: Marcenaria"
              />
            </Field>
            <ToggleRow style={{ marginTop: 8 }}>
              <EntradaOculta
                type="checkbox"
                checked={ocultarPrecos}
                onChange={(e) => setOcultarPrecos(e.target.checked)}
              />
              <ToggleSwitch $on={ocultarPrecos} />
              <span>Ocultar preços e valores monetários</span>
            </ToggleRow>
            {ocultarPrecos &&
              (formatosSel.has("png") || formatosSel.has("jpg")) && (
                <AvisoImagemComValores role="note">
                  Vale para PDF, Excel e CSV. Imagens PNG/JPG são capturas da
                  tela e saem <strong>com</strong> os valores visíveis.
                </AvisoImagemComValores>
              )}

            <ModalFooter>
              <Button type="button" $variant="secondary" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={salvar}
                disabled={Boolean(impedimento)}
                title={impedimento || undefined}
              >
                <FiSave size={14} />
                {editando ? "Salvar alterações" : "Criar agendamento"}
              </Button>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
    </AnimatePresence>
  );
}
